import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Box, Chip, Paper, Stack, Typography} from 'views/components';
import {ArrowDownwardOutlined, ArrowUpwardOutlined} from 'views/icons';
import {Translate} from 'views/module';

import KeyMetricsSkeleton from './skeletons/KeyMetricsSkeleton';

const KeyMetricsSection = (props) => {
  const {
    isLoading,
    errorMessage,
    keyMetricCards,
    sectionTitleExpression,
  } = props;

  return (
    <Box>
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 600,
          lineHeight: '14px',
          letterSpacing: '0.04em',
          color: '#A8A49E',
          mb: 2,
        }}
      >
        <Translate expression={sectionTitleExpression} />
      </Typography>
      {
        isLoading &&
        <KeyMetricsSkeleton />
      }
      {
        !isLoading &&
        errorMessage &&
        <Alert severity='warning'>
          {errorMessage}
        </Alert>
      }
      {
        !isLoading &&
        !errorMessage &&
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {xs: '1fr', md: 'repeat(3, minmax(0, 1fr))'},
              gap: 1.75,
            }}
          >
            {
              keyMetricCards.map((metric) => {
                const maxValue = Math.max(1, ...metric.series);
                const trendColor = metric.delta > 0 ? '#21892C' : metric.delta < 0 ? '#DA7955' : '#919191';
                const trendBackgroundColor = metric.delta > 0 ? '#E3FAE5' : metric.delta < 0 ? '#FFF1EC' : '#F2F0EC';
                const deltaPrefix = metric.deltaPercentage > 0 ? '+' : metric.deltaPercentage < 0 ? '-' : '';
                const absoluteDeltaPercentage = Math.abs(metric.deltaPercentage);
                const formattedDeltaPercentage = Math.abs(metric.deltaPercentage % 1) < 0.01 ?
                  absoluteDeltaPercentage.toFixed(0) :
                  absoluteDeltaPercentage.toFixed(1);
                const formattedPercentage = `${deltaPrefix}${formattedDeltaPercentage}%`;
                const pointList = metric.series.map((value, index) => {
                  const x = metric.series.length === 1 ? 48 : (index / (metric.series.length - 1)) * 96;
                  const y = 32 - ((value / maxValue) * 28);

                  return `${x},${y}`;
                }).join(' ');
                const areaPoints = `${pointList} 96,32 0,32`;

                return (
                  <Paper
                    key={metric.label}
                    sx={{
                      p: 2.5,
                      borderRadius: '8px',
                      border: '1px solid #E5E5E5',
                      boxShadow: 'none',
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Stack direction='row' justifyContent='space-between' alignItems='flex-start' spacing={2}>
                        <Box sx={{minWidth: 0}}>
                          <Typography
                            sx={{
                              fontSize: '11px',
                              fontWeight: 600,
                              lineHeight: '14px',
                              letterSpacing: '0.04em',
                              color: '#A8A49E',
                              mb: 1.5,
                            }}
                          >
                            {metric.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '24px',
                              fontWeight: 600,
                              lineHeight: '31px',
                              color: '#1A1916',
                            }}
                          >
                            {metric.currentCount}
                          </Typography>
                        </Box>
                        <Box
                          component='svg'
                          viewBox='0 0 96 32'
                          sx={{
                            width: 96,
                            height: 32,
                            flexShrink: 0,
                          }}
                        >
                          <polygon points={areaPoints} fill='rgba(218, 121, 85, 0.12)' />
                          <polyline
                            points={pointList}
                            fill='none'
                            stroke='#DA7955'
                            strokeWidth='2'
                            strokeLinejoin='round'
                            strokeLinecap='round'
                          />
                        </Box>
                      </Stack>
                      <Box sx={{borderTop: '1px solid #EAEAEA'}} />
                      <Stack direction='row' gap={1} alignItems='center'>
                        <Chip
                          icon={
                            metric.delta >= 0 ?
                              <ArrowUpwardOutlined sx={{fontSize: '14px !important', color: trendColor}} /> :
                              <ArrowDownwardOutlined sx={{fontSize: '14px !important', color: trendColor}} />
                          }
                          label={formattedPercentage}
                          sx={{
                            'height': 22,
                            'backgroundColor': trendBackgroundColor,
                            'borderRadius': '20px',
                            'color': trendColor,
                            'fontSize': '11px',
                            'fontWeight': 500,
                            '& .MuiChip-label': {
                              px: 1,
                            },
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '11px',
                            fontWeight: 500,
                            lineHeight: '14px',
                            color: '#A8A49E',
                          }}
                        >
                          <Translate
                            expression='metrics.LeadFunnel.KeyMetrics.Previous'
                            substitute={{count: metric.previousCount}}
                          />
                        </Typography>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })
            }
          </Box>
        </Stack>
      }
    </Box>
  );
};

KeyMetricsSection.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string.isRequired,
  keyMetricCards: PropTypes.array.isRequired,
  sectionTitleExpression: PropTypes.string.isRequired,
};

export default React.memo(KeyMetricsSection);
